// atomic types
export type Properties = Record<string, any>;

export const CitOrigin = {
  HUB: 'hub',
  HUBPRO: 'hubpro',
  IFRAME: 'iframe'
} as const;

export type CitOrigin = (typeof CitOrigin)[keyof typeof CitOrigin];

export interface PaginatedResponse<T> {
  size: number;
  total: number;
  cursor?: string;
  results: T[];
}

export interface PaginatedSearchResponse<T> {
  size: number;
  total: number;
  cursor?: string;
  entries: T[];
}

export interface ClassificationCount {
  classification: string;
  total: number;
}

export interface ProfileType {
  account_id: string;
  account_slug: string;

  total: number;

  type: string;
  type_label: string;
  type_description?: string;
  media?: Media;
}

/**
 * Media
 */
export interface MediaDerivative {
  path: string;
  width: number;
  height: number;
}

export interface Media {
  uuid: string;
  content_type: string;
  name?: string;
  description?: string;
  derivatives?: {
    // [key in DerivativeType]: MediaDerivative;
    public_thumbnail: MediaDerivative;
    public_thumbnail_2x: MediaDerivative;
    public_original: { path: string };
    public_full: { path: string };
    public_poster: MediaDerivative;
    public: MediaDerivative;
    public_2x: MediaDerivative;
    public_highres: MediaDerivative;
    public_highres_2x: MediaDerivative;
    dominant_color: string;
    complementary_color: string;
  };
}

export interface Classification {
  classification: string;
  total: number;
  media: Media[];
}

/**
 * Entry
 */
export interface Entry {
  id: string;
  slug: string;

  account_id: string;
  account_slug: string;

  classification: string;
  classification_v2: string;

  folder_ids: string[];

  properties: Properties;
  media?: Media[];

  next_id: string | null;
  prev_id: string | null;
}

/**
 * Profile
 */
export interface Profile {
  id: string;
  // always present, but not necessarily url-safe - run it through toUrlSafeSlug before using it in a link
  slug: string;

  account_id: string;
  account_slug: string;

  type_label: string;
  type: string;

  properties: Properties;
  media?: Media[];
}

/**
 * Folder
 */
export interface Folder {
  id: string;
  slug: string;
  name: string;

  account_id: string;

  total: number;
  classification: ClassificationCount[];

  description?: string;
  euid_representative?: string;
  media?: Media;
}

/**
 * Account
 */
export interface AccountMediaConfig {
  media_adornment?: 'square' | 'rounded';
  media_aspect_ratio?: 'natural' | 'square';
}

export interface Slot {
  on: boolean;
  property: string[];
}

export interface ThumbnailSlotCustomization {
  slot1?: Slot;
  slot2?: Slot;
  slot3?: Slot;
  slot4?: Slot;
}

// TODO: remove once we migrate to new theme shape
export interface OldThumbnailSlotCustomization {
  slot1?: string[];
  slot2?: string[];
  slot3?: string[];
}

export const FolderLayout = {
  GRID: 'grid',
  ROWS: 'rows'
} as const;

export type FolderLayout = (typeof FolderLayout)[keyof typeof FolderLayout];

export const MediaAspectRatio = {
  NATURAL: 'natural',
  SQUARE: 'square'
} as const;

export type MediaAspectRatio = (typeof MediaAspectRatio)[keyof typeof MediaAspectRatio];

export const MediaAdornment = {
  ROUNDED: 'rounded',
  SQUARE: 'square'
} as const;

export type MediaAdornment = (typeof MediaAdornment)[keyof typeof MediaAdornment];

export const ThumbnailSize = {
  SMALL: 100,
  MEDIUM: 150,
  LARGE: 200
} as const;

export type ThumbnailSize = (typeof ThumbnailSize)[keyof typeof ThumbnailSize];

// the iframe embed renders no hero, so it never carries a hero flag
export type HeroConfigOrigin = Exclude<CitOrigin, 'iframe'>;

// keyed by HeroConfigOrigin. An absent origin reads as enabled, matching the publishing
// config's on-by-default behavior
export type HeroConfigItem<T> = { [K in HeroConfigOrigin]?: boolean } & {
  value?: T;
};

// the hero fields, and the single runtime source of truth for what they are. HeroConfig
// derives its keys from these, so the two cannot drift
export const HeroField = {
  BACKGROUND: 'background',
  AVATAR: 'avatar',
  NAME: 'name',
  LOCATION: 'location',
  WEBSITE_URL: 'website_url',
  DESCRIPTION: 'description',
  TOTAL: 'total'
} as const;

export type HeroField = (typeof HeroField)[keyof typeof HeroField];

// the value each hero field carries. HeroConfig indexes this by HeroField, so a new field
// cannot be added without also giving it a value type
interface HeroFieldValues {
  background: Media;
  avatar: Media;
  name: string;
  location: string;
  website_url: string;
  description: string;
  total: number;
}

// per-origin visibility, and the published value, of each hero field on the HUB and HUB Pro
export type HeroConfig = {
  [K in HeroField]?: HeroConfigItem<HeroFieldValues[K]>;
};

export type HeaderHTML = {
  custom: boolean;
  html: string;
};

export interface AccountThemeConfig {
  isTestTheme?: boolean;
  // TODO: remove these once we migrate to the new color object
  backgroundColor?: string;
  bannerTextColor?: string;
  color?: {
    titleFontColor?: string;
    bodyFontColor?: string;
    bannerTextColor?: string;
    backgroundColor?: string;
    entryCardColor?: string;
  };
  layout?: {
    folderLayout?: FolderLayout;
    thumbnailSlots?: ThumbnailSlotCustomization;
    thumbnailSizes?: ThumbnailSize;
    gap?: number;
  };
  typography?: {
    titleFont?: string;
    bodyFont?: string;
    letterSpacing?: number | 'normal';
    lineHeight?: number | 'normal';
    baseFontSize?: number | 'medium';
  };
  style?: {
    mediaAspectRatio?: MediaAspectRatio;
    mediaAdornment?: MediaAdornment;
  };
  header?: {
    headerFont?: string;
    headerFontColor?: string;
    headerBackgroundColor?: string;
    headerHTML?: HeaderHTML;
    heroConfig?: HeroConfig;
  };
}

export interface AccountCustomizationConfig {
  sort?: {
    path: string;
    order: 'asc' | 'desc';
  };
  features?: {
    download?: boolean;
    playback?: boolean;
  };
  // TODO: remove after we migrate to new theme shape
  thumbnail: OldThumbnailSlotCustomization;
}

export interface Account {
  id: string;
  slug: string;
  // sent for hubpro and iframe only
  name?: string;
  // sent for hubpro only
  avatar?: Media;
  folders: Folder[];
  classifications: ClassificationCount[];
  media?: AccountMediaConfig;
  customization?: AccountCustomizationConfig;
  theme?: AccountThemeConfig;
  metrics?: {
    ga_measurement_id?: string;
  };

  host?: string;
  subdomain?: string;
}

export interface AccountSearchResults {
  entries: Entry[];
  profiles: Profile[];
  folders: Folder[];
  total: number;
}

export interface AllSearchResults extends AccountSearchResults {
  accounts: Account[];
}
